import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { User } from '../types';
import { User as UserIcon, X, Search } from 'lucide-react';

interface UserImpersonationProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const UserImpersonation: React.FC<UserImpersonationProps> = ({ open: controlledOpen, onOpenChange }) => {
  const { user, isImpersonating, originalUser, startImpersonation, stopImpersonation } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined && onOpenChange !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled ? onOpenChange : setInternalOpen;
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if current user (or original user if impersonating) is superuser
  const isSuperuser = user?.is_superuser || originalUser?.is_superuser || false;

  useEffect(() => {
    if (isOpen && isSuperuser) {
      loadUsers();
    }
  }, [isOpen, isSuperuser]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const allUsers = await apiService.getAllUsers();
      setUsers(allUsers);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load users';
      setError(errorMessage);
      console.error('Error loading users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartImpersonation = async (targetUserId: number) => {
    try {
      await startImpersonation(targetUserId);
      setIsOpen(false);
      setSearchTerm('');
    } catch (err: any) {
      setError(err.message || 'Failed to start impersonation');
    }
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      setIsOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to stop impersonation');
    }
  };

  const filteredUsers = users.filter((u) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      u.username.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchLower)
    );
  });

  // Don't show component if not superuser
  if (!isSuperuser) {
    return null;
  }

  return (
    <>
      {/* Impersonation Button - only when not controlled (e.g. standalone in nav) */}
      {!isControlled && !isImpersonating && (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md transition-colors"
          title="Impersonate User"
        >
          <UserIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Impersonate</span>
        </button>
      )}

      {/* Impersonation Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {isImpersonating ? 'Stop Impersonation' : 'Impersonate User'}
              </h2>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm('');
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isImpersonating ? (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-sm text-yellow-800">
                      You are currently impersonating <strong>{user?.username}</strong>
                    </p>
                    {originalUser && (
                      <p className="text-sm text-yellow-700 mt-1">
                        Original user: <strong>{originalUser.username}</strong>
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleStopImpersonation}
                    className="w-full btn-primary"
                  >
                    Stop Impersonating
                  </button>
                </div>
              ) : (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search by username, email, or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  {/* Users List */}
                  {isLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading users...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No users found</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredUsers.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleStartImpersonation(u.id)}
                          className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-primary transition-colors"
                          disabled={u.id === (originalUser?.id ?? user?.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">
                                {u.first_name} {u.last_name}
                              </p>
                              <p className="text-sm text-gray-500">{u.username}</p>
                              <p className="text-sm text-gray-400">{u.email}</p>
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                {u.role}
                              </span>
                            </div>
                            {u.id === originalUser?.id && (
                              <span className="text-xs text-gray-400">(You)</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserImpersonation;
